import { useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import {
  DEFAULT_TIMEZONE,
  DEFAULT_WEEKLY_SCHEDULE,
  toTimezoneEnumValue,
  type WeeklySchedule,
} from '@super/shared-types'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  TimezoneSelector,
  type TimezoneEnumValue,
} from '@/components/common/TimezoneSelector'
import { WeeklyHoursSchedule } from '@/components/common/WeeklyHoursSchedule'
import { FloatingActionIslandContent } from '@/components/ui/FloatingActionIslandContent'
import { useOnboarding } from '../onboarding-context'
import { findStepByRoute, nextStep } from '../flow.config'

export function CompanyStep() {
  const navigate = useNavigate()
  const location = useLocation()
  const { state, patch } = useOnboarding()

  const [companyName, setCompanyName] = useState(state.name ?? '')
  const [doors, setDoors] = useState(
    state.doors != null ? String(state.doors) : '',
  )
  const [timezone, setTimezone] = useState<TimezoneEnumValue>(
    toTimezoneEnumValue(state.timezoneName ?? undefined) ?? DEFAULT_TIMEZONE,
  )
  const [officeHours, setOfficeHours] = useState<WeeklySchedule>(
    state.officeHours ?? DEFAULT_WEEKLY_SCHEDULE,
  )
  const [saving, setSaving] = useState(false)

  const nameValid = companyName.trim().length >= 2

  const parsedDoors = useMemo(() => {
    const digits = doors.replace(/[^0-9]/g, '')
    if (!digits) return null
    const n = Number.parseInt(digits, 10)
    return Number.isFinite(n) ? n : null
  }, [doors])

  const handleContinue = async () => {
    if (!nameValid || saving) return
    setSaving(true)
    try {
      await patch({
        name: companyName.trim(),
        timezoneName: timezone,
        doors: parsedDoors ?? undefined,
        officeHours,
      })
      const current = findStepByRoute(location.pathname)
      const next = current ? nextStep(current, state.userType ?? null) : null
      if (next) navigate(next.route)
    } catch {
      toast.error("Couldn't save your details. Try again.")
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="mx-auto flex w-full max-w-[640px] flex-col gap-6">
      <div className="space-y-2 text-center">
        <p className="text-muted-foreground font-fragment-mono text-[10px] uppercase tracking-[0.14em]">
          SUPER · COMPANY
        </p>
        <h1 className="font-space-grotesk text-3xl font-semibold tracking-tight">
          Your company
        </h1>
        <p className="text-muted-foreground text-sm">
          Let's learn a bit about your company. This will help your agents with
          the right context.
        </p>
      </div>

      <div className="flex flex-col gap-4">
        <label className="flex flex-col gap-1.5">
          <span className="ml-3 text-xs font-medium">Company name</span>
          <Input
            value={companyName}
            onChange={e => setCompanyName(e.target.value)}
            placeholder="Acme Property Management"
            autoFocus
          />
        </label>

        <div className="flex gap-3">
          <label className="flex flex-1 flex-col gap-1.5">
            <span className="ml-3 text-xs font-medium">Doors managed</span>
            <Input
              value={doors}
              onChange={e => setDoors(e.target.value)}
              inputMode="numeric"
            />
          </label>
          <div className="flex flex-1 flex-col gap-1.5">
            <span className="ml-3 text-xs font-medium">Time zone</span>
            <TimezoneSelector value={timezone} onValueChange={setTimezone} />
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <span className="ml-3 text-xs font-medium">Business hours</span>
          <WeeklyHoursSchedule value={officeHours} onChange={setOfficeHours} />
        </div>
      </div>

      <FloatingActionIslandContent
        show={true}
        componentId="onboarding-company"
        priority={10}
      >
        <div className="pointer-events-auto">
          <Button
            onClick={handleContinue}
            disabled={!nameValid || saving}
            size="sm"
          >
            {saving ? 'Saving…' : 'Continue →'}
          </Button>
        </div>
      </FloatingActionIslandContent>
    </div>
  )
}
